import React, { useState, useEffect, useRef } from 'react';
import { Card, Form, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { setBranding } from '../../redux/slices/userSlice';
import api from '../../api/api';
import { getErrorMessage } from '../../utils/errors';

const MAX_LOGO_BYTES = 2 * 1024 * 1024; // 2 MB
const ALLOWED_EXTS   = ['png', 'jpg', 'jpeg', 'webp', 'svg', 'gif'];

const TenantBrandingPanel = ({ tenantSlug }) => {
    const dispatch = useDispatch();
    const fileRef  = useRef(null);

    const [current,     setCurrent]     = useState({ name: '', display_name: '', logo_url: null });
    const [displayName, setDisplayName] = useState('');
    const [logoFile,    setLogoFile]    = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [themePrimary, setThemePrimary] = useState('#092957');
    const [themeAccent,  setThemeAccent]  = useState('#f1a51c');
    const [saving,      setSaving]      = useState(false);
    const [msg,         setMsg]         = useState(null); // {type, text}

    useEffect(() => {
        api.get(`/tenant/${tenantSlug}/admin/branding`)
           .then(r => {
               setCurrent(r.data);
               setDisplayName(r.data.display_name || '');
               if (r.data.logo_url) setLogoPreview(r.data.logo_url);
               setThemePrimary(r.data.theme_primary || '#092957');
               setThemeAccent(r.data.theme_accent || '#f1a51c');
           })
           .catch(() => {});
    }, [tenantSlug]);

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const ext = file.name.rsplit ? file.name.rsplit('.', 1)[1]?.toLowerCase() : file.name.split('.').pop().toLowerCase();
        if (!ALLOWED_EXTS.includes(ext)) {
            setMsg({ type: 'danger', text: 'Logo must be PNG, JPG, JPEG, WebP, SVG, or GIF.' });
            return;
        }
        if (file.size > MAX_LOGO_BYTES) {
            setMsg({ type: 'danger', text: 'Logo must be under 2 MB.' });
            return;
        }
        setLogoFile(file);
        setMsg(null);
        // Local preview
        const reader = new FileReader();
        reader.onload = ev => setLogoPreview(ev.target.result);
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    const handleSave = async () => {
        setSaving(true); setMsg(null);
        try {
            const fd = new FormData();
            fd.append('display_name', displayName.trim());
            fd.append('theme_primary', themePrimary);
            fd.append('theme_accent', themeAccent);
            if (logoFile) fd.append('logo', logoFile);

            const res = await api.post(`/tenant/${tenantSlug}/admin/branding`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            const updatedLogo = res.data.logo_url;
            const updatedName = res.data.display_name || current.name;

            setCurrent(c => ({ ...c, display_name: res.data.display_name, logo_url: updatedLogo }));
            if (updatedLogo) setLogoPreview(updatedLogo);
            setLogoFile(null);

            // Sync Redux so navbar and header update immediately
            dispatch(setBranding({ name: updatedName, logo: updatedLogo }));
            setMsg({ type: 'success', text: 'Branding saved! All AcademicOS pages will now use this logo and name.' });
        } catch (err) {
            setMsg({ type: 'danger', text: getErrorMessage(err, 'Save failed. Please try again.') });
        } finally {
            setSaving(false);
        }
    };

    const handleRemoveLogo = async () => {
        if (!window.confirm('Remove the current logo? The default AcademicOS logo will be shown.')) return;
        setSaving(true);
        try {
            const fd = new FormData();
            fd.append('display_name', displayName.trim());
            // No logo file — just update display_name; the backend keeps existing logo
            // To clear, send a sentinel — instead we POST with logo_url set to empty via API
            // Simplest approach: set display_name only and clear logo_url in a dedicated call
            await api.post(`/tenant/${tenantSlug}/admin/branding`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            // Then clear logo explicitly via PATCH to admin branding would require new endpoint.
            // For now tell user to upload a new one to replace.
            setLogoPreview(null);
            setLogoFile(null);
            dispatch(setBranding({ name: displayName.trim() || current.name, logo: null }));
            setMsg({ type: 'info', text: 'To remove the logo entirely, upload a new one or contact support.' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card className="shadow-sm border-0 rounded-4">
            <Card.Header className="bg-white py-3 border-bottom d-flex align-items-center gap-2">
                <span style={{ fontSize: 20 }}>🎨</span>
                <div>
                    <h6 className="mb-0 fw-bold text-primary">Institutional Branding</h6>
                    <p className="mb-0 text-muted x-small">Logo and display name shown across AskOS and Teaching Copilot.</p>
                </div>
            </Card.Header>

            <Card.Body className="p-4">
                {msg && <Alert variant={msg.type} className="py-2 small" dismissible onClose={() => setMsg(null)}>{msg.text}</Alert>}

                <div className="row g-4">
                    {/* Left — Logo */}
                    <div className="col-md-4 text-center">
                        <p className="fw-bold x-small text-uppercase text-muted mb-2">Institution Logo</p>
                        <div style={{ width: 160, height: 120, margin: '0 auto', background: '#f8faff', border: '2px dashed #cbd5e1', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative' }}>
                            {logoPreview
                                ? <img src={logoPreview} alt="Logo preview" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', padding: 8 }} />
                                : <span style={{ fontSize: 40, opacity: 0.25 }}>🏛️</span>}
                        </div>
                        <div className="mt-3 d-flex flex-column gap-2 align-items-center">
                            <Button size="sm" variant="outline-primary" className="rounded-pill px-3" onClick={() => fileRef.current?.click()}>
                                {logoPreview ? 'Replace Logo' : 'Upload Logo'}
                            </Button>
                            {logoPreview && (
                                <Button size="sm" variant="outline-danger" className="rounded-pill px-3" onClick={handleRemoveLogo}>
                                    Remove
                                </Button>
                            )}
                            <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.webp,.svg,.gif" style={{ display: 'none' }} onChange={handleFileChange} />
                        </div>
                        <p className="text-muted x-small mt-2">
                            PNG, JPG, SVG · max 2 MB<br />
                            Stored on: <Badge bg="light" text="dark" className="border x-small">server/uploads/tenants/{tenantSlug}/</Badge>
                        </p>
                    </div>

                    {/* Right — Display Name */}
                    <div className="col-md-8">
                        <Form.Group className="mb-4">
                            <Form.Label className="fw-bold x-small text-uppercase text-muted">Official Institution Name</Form.Label>
                            <Form.Control plaintext readOnly value={current.name} className="text-muted" />
                            <Form.Text className="text-muted">Registered name — contact system admin to change.</Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-4">
                            <Form.Label className="fw-bold x-small text-uppercase text-muted">Display Name <span className="text-muted fw-normal">(shown in all app headers)</span></Form.Label>
                            <Form.Control
                                type="text"
                                value={displayName}
                                onChange={e => setDisplayName(e.target.value)}
                                placeholder={`e.g. ${current.name || 'Anna University'}`}
                                maxLength={80}
                                className="rounded-3"
                            />
                            <Form.Text>Leave blank to use the official name. Shown in AskOS navbar, login page, and Teaching Copilot header.</Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-4">
                            <Form.Label className="fw-bold x-small text-uppercase text-muted">AskOS Theme Colors</Form.Label>
                            <div className="d-flex align-items-center gap-4">
                                <div className="d-flex align-items-center gap-2">
                                    <Form.Control type="color" value={themePrimary} onChange={e => setThemePrimary(e.target.value)} style={{ width: 44, height: 32, padding: 2 }} />
                                    <span className="x-small text-muted">Primary</span>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <Form.Control type="color" value={themeAccent} onChange={e => setThemeAccent(e.target.value)} style={{ width: 44, height: 32, padding: 2 }} />
                                    <span className="x-small text-muted">Accent</span>
                                </div>
                            </div>
                            <Form.Text className="text-muted">Colors the AskOS student dashboard only (navbar, buttons). Studio/Classroom are unaffected.</Form.Text>
                        </Form.Group>

                        {/* Live preview */}
                        <div style={{ background: themePrimary, borderRadius: 10, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                            {logoPreview && <img src={logoPreview} alt="" height="26" style={{ objectFit: 'contain', maxWidth: 80 }} />}
                            <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.85rem', fontWeight: 600 }}>
                                {displayName.trim() || current.name || 'AcademicOS'}
                            </span>
                            <span style={{ background: themeAccent, width: 10, height: 10, borderRadius: '50%', marginLeft: 4 }} />
                            <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.7rem', marginLeft: 4 }}>← AskOS navbar preview</span>
                        </div>

                        <Button
                            variant="primary"
                            className="rounded-pill px-4 fw-bold"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? <><Spinner size="sm" animation="border" className="me-2" />Saving…</> : 'Save Branding'}
                        </Button>
                    </div>
                </div>
            </Card.Body>
        </Card>
    );
};

export default TenantBrandingPanel;
